import React, { useState, Fragment, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { Button, Box, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';

import Title from 'components/common/Title';
import { ActionButton } from 'components/common/Buttons';
import { ENDPOINTS_GRID_COLUMNS } from './utils';
import { PERMISSIONS } from '../../constants';
import { useHasPermissions } from 'hooks/useHasPermissions';
import {
  useCatchTrafficRequestMutation,
  useCatchTrafficResponseMutation,
  useGetEndpointsListQuery,
  useUpdateEndpointStatusMutation,
} from '../../store/api/endpoint.api';
import { useSnackbar } from 'notistack';

const STATUS_OPTS = [
  ['ACTIVE', 'activate'],
  ['PAUSED', 'pause'],
  ['REMOVED', 'remove'],
];

const StyledTableContainer = styled(Box)(() => ({
  width: '100%',
  ['& tbody .MuiTableCell-body']: {
    whiteSpace: 'nowrap',
  },
}));

export default function DataTable() {
  const canEditEndpoints = useHasPermissions(PERMISSIONS.PUBLISHERS_MANAGEMENT);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 100,
  });

  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const endpointsParams = useMemo(
    () => ({
      ...filters,
      page: pagination.pageIndex,
      limit: pagination.pageSize,
    }),
    [filters, pagination.pageIndex, pagination.pageSize],
  );
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: { endpoints = [], count = 0, totals = null } = {},
    isFetching,
  } = useGetEndpointsListQuery(endpointsParams);
  const [updateStatus] = useUpdateEndpointStatusMutation();
  const [catchTrafficRequest] = useCatchTrafficRequestMutation();
  const [catchTrafficResponse] = useCatchTrafficResponseMutation();

  const handleTrafficResponse = useCallback(
    async (id) => {
      try {
        await catchTrafficResponse(id).unwrap();
        enqueueSnackbar(`Captured traffic response for ${id} successfully`, {
          variant: 'success',
        });
      } catch (err) {
        enqueueSnackbar(`Failed to capture traffic response for ${id}`, {
          variant: 'error',
        });
      }
    },
    [catchTrafficResponse, enqueueSnackbar],
  );
  const handleTrafficRequest = useCallback(
    async (id) => {
      try {
        await catchTrafficRequest(id).unwrap();
        enqueueSnackbar(`Captured traffic request for ${id} successfully`, {
          variant: 'success',
        });
      } catch (err) {
        enqueueSnackbar(`Failed to capture traffic request for ${id}`, {
          variant: 'error',
        });
      }
    },
    [catchTrafficRequest, enqueueSnackbar],
  );

  const applyFilters = useCallback(() => {
    setFilters({ searchQuery, ts: Date.now() });
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchQuery, setFilters, setPagination]);

  const extractEndpointIds = () =>
    endpointsTable.getSelectedRowModel().rows.map((row) => row.original.id);

  const onChangeStatus = async (e) => {
    try {
      const ids = extractEndpointIds();

      await updateStatus({
        status: e.currentTarget.dataset.status,
        ids: ids,
      }).unwrap();

      endpointsTable.resetRowSelection();
      enqueueSnackbar(`${ids.length} Endpoint(s) updated`, {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(`Failed to update endpoint status`, {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () =>
      ENDPOINTS_GRID_COLUMNS({
        handleTrafficResponse,
        handleTrafficRequest,
      }),
    [handleTrafficRequest, handleTrafficResponse],
  );

  const endpointsTable = useMaterialReactTable({
    columns,
    data: endpoints,
    rowCount: count,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableFilters: false,
    enableGlobalFilter: false,
    columnFilterDisplayMode: 'popover',
    positionToolbarAlertBanner: 'bottom',
    manualPagination: true,
    onPaginationChange: setPagination,
    meta: {
      totals,
    },
    muiPaginationProps: {
      rowsPerPageOptions: [100, 500, 1000],
    },
    state: {
      isLoading: isFetching,
      pagination,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
        }}
      >
        <Box display='flex' gap='16px' alignItems='center' flexGrow={1}>
          <TextField
            placeholder='Search...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                applyFilters();
              }
            }}
            size='small'
            sx={{ width: '400px' }}
          />

          <Button
            variant='contained'
            color='primary'
            sx={{ whiteSpace: 'nowrap' }}
            onClick={applyFilters}
          >
            Apply
          </Button>

          {table.getSelectedRowModel().rows.length > 0 &&
            STATUS_OPTS.map(([targetStatus, label]) => (
              <ActionButton
                key={targetStatus}
                disabled={!canEditEndpoints}
                onClick={onChangeStatus}
                variant='text'
                data-status={targetStatus}
                sx={{ textTransform: 'capitalize' }}
              >
                {label}
              </ActionButton>
            ))}
        </Box>
        <Button
          variant='contained'
          color='primary'
          component={Link}
          to='/endpoints/create'
          sx={{ whiteSpace: 'nowrap' }}
          disabled={!canEditEndpoints}
        >
          Add New
        </Button>
      </Box>
    ),
  });

  return (
    <Fragment>
      <Box>
        <Title>Publisher Endpoints</Title>
      </Box>
      <StyledTableContainer>
        <MaterialReactTable table={endpointsTable} />
      </StyledTableContainer>
    </Fragment>
  );
}
